/**
 * Root-Provider-Tree.
 *
 * Reihenfolge (outside → inside):
 *   1. QueryClientProvider  — Cache für alle API-Calls
 *   2. Toaster              — sonner-Notifications (Portal in body)
 *
 * Auth-Store + Router werden separat eingehängt (Router-Provider in main.tsx,
 * AuthStore ist Singleton außerhalb React, siehe shared/auth/store.ts).
 */
import { HTTPError } from 'ky';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import type { ReactNode } from 'react';
import { Toaster } from '@/shared/components';
import { resetOnIdentityChange } from '../identityReset';

// QueryClient ist Singleton — niemals pro Render neu erzeugen.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      retry: (failureCount, error: unknown) => {
        // Antworten, die ein zweiter Versuch nicht ändert, nicht wiederholen (401 erneuert der
        // HTTP-Client selbst). ky trägt den Status an `response`, nicht am Fehler – bisher griff
        // die Regel deshalb nie, und jeder Fehlerzustand kam erst nach drei Anläufen.
        const status =
          error instanceof HTTPError
            ? error.response.status
            : error && typeof error === 'object' && 'status' in error
              ? (error as { status: number }).status
              : 0;
        if ([400, 401, 403, 404, 409, 422].includes(status)) return false;
        return failureCount < 2;
      },
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: false,
    },
  },
});
resetOnIdentityChange(queryClient);

export function AppProviders({ children }: { children: ReactNode }) {
  return (
    <QueryClientProvider client={queryClient}>
      {children}
      <Toaster />
      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  );
}
